import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from '../icons/Icon';
import { PermissionGuard } from '../permissions/PermissionGuard';
import styles from './AddTagControl.module.css';
import { AddTagPicker, type AddTagPickerProps } from './AddTagPicker';

export interface AddTagControlProps
    extends Omit<AddTagPickerProps, 'aria-label' | 'placeholder' | 'defaultOpen' | 'onDismiss'> {
    /** The permission adding a tag needs: hidden when the role can never hold it, refused with its reason when blocked. */
    readonly permission: string;
    /** The search's placeholder — *Find or create a lead tag*. */
    readonly placeholder: string;
}

/**
 * *Add tag* at the end of a record's tags (the tag design of the UI revamp preview): a dashed **+ Add tag** chip the
 * height of a tag, which turns into the tag search (`AddTagPicker`, its list open and the input focused) when
 * pressed, in place of a labelled form field under the list. A pick adds the tag and folds the search back to the
 * chip, with focus on it; `Escape` with the list closed does the same; moving focus away folds it back and leaves
 * focus where it went. The typed text is cleared each time it folds.
 *
 * The chip carries `data-tag-add`, which the sections' focus repair looks for after the last tag is removed. Test
 * ids: the chip is `<testId>-open`, the search input `<testId>`, as the picker's.
 */
export function AddTagControl({
    permission,
    placeholder,
    onInputChange,
    onPick,
    ...picker
}: AddTagControlProps) {
    const [searching, setSearching] = useState(false);
    const chipRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);
    const focusChip = useRef(false);

    useLayoutEffect(() => {
        if (searching) {
            searchRef.current?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
        } else if (focusChip.current) {
            focusChip.current = false;
            chipRef.current?.focus();
        }
    }, [searching]);

    function fold(toChip: boolean) {
        // Never unset here: removing the focused input can report a blur after a pick has asked for the chip.
        if (toChip) {
            focusChip.current = true;
        }
        setSearching(false);
        onInputChange('');
    }
    const foldRef = useRef(fold);
    useLayoutEffect(() => {
        foldRef.current = fold;
    });

    // Focus leaving the search (and its list, which the Popover portals elsewhere) folds it back to the chip. Leaving
    // the window (another window or tab taking focus) is not leaving the search: the typed text stays, and focus
    // returns to it with the window.
    useEffect(() => {
        const search = searchRef.current;
        if (!searching || search === null) {
            return undefined;
        }
        const onFocusOut = (event: FocusEvent) => {
            if (!document.hasFocus()) {
                return;
            }
            const next = event.relatedTarget;
            const stays =
                next instanceof Element &&
                (search.contains(next) || next.closest('[data-slot="popover-content"]') !== null);
            if (!stays) {
                foldRef.current(false);
            }
        };
        search.addEventListener('focusout', onFocusOut);
        return () => search.removeEventListener('focusout', onFocusOut);
    }, [searching]);

    if (!searching) {
        return (
            <PermissionGuard permission={permission}>
                <button
                    className={styles.chip}
                    data-tag-add
                    data-testid={`${picker['data-testid']}-open`}
                    onClick={() => setSearching(true)}
                    ref={chipRef}
                    type="button"
                >
                    <Icon name="add" size="sm" />
                    Add tag
                </button>
            </PermissionGuard>
        );
    }

    return (
        <div className={styles.search} ref={searchRef}>
            <AddTagPicker
                {...picker}
                aria-label="Add tag"
                defaultOpen
                onDismiss={() => fold(true)}
                onInputChange={onInputChange}
                onPick={(id) => {
                    onPick(id);
                    fold(true);
                }}
                placeholder={placeholder}
            />
        </div>
    );
}
